import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Package, Receipt, Star } from "lucide-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  POST_PAYMENT_ORDER_STATUSES,
  customerOrderStatusLabel,
  customerOrderStatusColorKey,
  marketplaceFulfilmentMethodLabel,
  marketplaceFulfilmentStatusLabel,
} from "@gaole/shared";
import { api } from "../lib/api";
import { rm, fmtOrderListDate, credits } from "../lib/format";
import { Badge, Button, EmptyState, PageLoader, statusColor } from "../components/ui";
import { PurchaseReviewForm } from "../components/reviews/PurchaseReviewForm";
import { StarRating } from "../components/reviews/StarRating";
import { Countdown } from "../components/Countdown";
import { PageHeader } from "../components/storefront/PageHeader";
import { PickupScheduleCallout } from "../components/pickup/PickupScheduleCallout";
import type { OrderPickupDetails } from "@gaole/shared";
import { OrderDocumentsModal } from "../components/storefront/OrderDocumentsModal";
import { useAuth } from "../stores/auth";

interface OrderListItem {
  id: string;
  orderNo: string;
  kind: string;
  status: string;
  total: number;
  creditUsed: number;
  paidAt: string | null;
  paymentDeadline: string | null;
  trackingNo: string | null;
  courier?: string | null;
  createdAt: string;
  latestReceiptStatus: string | null;
  sellerFulfilmentStatus?: string | null;
  fulfilmentMethod?: string | null;
  pickupDetails?: OrderPickupDetails | null;
  items: {
    id: string;
    name: string;
    quantity: number;
    canReview?: boolean;
    review?: {
      id: string;
      rating: number;
      comment: string | null;
      images: string[];
      createdAt: string;
    } | null;
  }[];
}

function reviewableItemCount(order: OrderListItem): number {
  return order.items.filter((i) => i.canReview).length;
}

type OrdersTab = "all" | "active" | "past";

const PAST_STATUSES = new Set(["DELIVERED", "CANCELLED", "REFUNDED", "FORFEITED"]);

const HIDDEN_UNLESS_TOGGLED = new Set(["CANCELLED", "FORFEITED"]);

function isHiddenUnlessToggled(order: OrderListItem): boolean {
  return HIDDEN_UNLESS_TOGGLED.has(order.status);
}

const TABS: { id: OrdersTab; label: string }[] = [
  { id: "all", label: "All" },
  { id: "active", label: "Active" },
  { id: "past", label: "Past" },
];

function headerBandClass(color: ReturnType<typeof statusColor>): string {
  switch (color) {
    case "green":
      return "bg-emerald-500/10 border-emerald-500/20";
    case "amber":
      return "bg-amber-500/10 border-amber-500/20";
    case "blue":
      return "bg-brand-blue/15 border-brand-blue/25";
    case "red":
      return "bg-red-500/10 border-red-500/20";
    case "pink":
      return "bg-brand-pink/10 border-brand-pink/20";
    default:
      return "bg-white/5 border-white/10";
  }
}

function isPostPayment(status: string): boolean {
  return POST_PAYMENT_ORDER_STATUSES.includes(status as (typeof POST_PAYMENT_ORDER_STATUSES)[number]);
}

/** Credit top-ups have no fulfilment — treat paid ones as past. */
function isPastOrder(order: OrderListItem): boolean {
  if (PAST_STATUSES.has(order.status)) return true;
  if (order.kind === "CREDIT_TOPUP" && isPostPayment(order.status)) return true;
  return false;
}

function matchesTab(order: OrderListItem, tab: OrdersTab): boolean {
  if (tab === "all") return true;
  if (tab === "past") return isPastOrder(order);
  return !isPastOrder(order);
}

export function Orders() {
  const { moduleEnabled, config } = useAuth();
  const collectLabel = config?.marketplaceCollectLabel ?? "COD";
  const queryClient = useQueryClient();
  const documentsOn = moduleEnabled("documents");
  const invoicingOn = moduleEnabled("invoicing");
  const [tab, setTab] = useState<OrdersTab>("all");
  const [showCancelledForfeited, setShowCancelledForfeited] = useState(false);
  const [docOrder, setDocOrder] = useState<{ id: string; orderNo: string } | null>(null);
  const [reviewOrderId, setReviewOrderId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ["orders"],
    queryFn: () => api<{ items: OrderListItem[] }>("/orders/mine"),
  });

  const invalidateOrders = () => {
    void queryClient.invalidateQueries({ queryKey: ["orders"] });
  };

  const items = data?.items ?? [];

  const visibleItems = useMemo(
    () => (showCancelledForfeited ? items : items.filter((o) => !isHiddenUnlessToggled(o))),
    [items, showCancelledForfeited],
  );

  const hiddenCount = useMemo(() => items.filter((o) => isHiddenUnlessToggled(o)).length, [items]);

  useEffect(() => {
    if (!reviewOrderId) return;
    const order = items.find((o) => o.id === reviewOrderId);
    if (order && reviewableItemCount(order) === 0) setReviewOrderId(null);
  }, [items, reviewOrderId]);
  const filtered = useMemo(
    () => visibleItems.filter((order) => matchesTab(order, tab)),
    [visibleItems, tab],
  );

  const tabCounts = useMemo(
    () => ({
      all: visibleItems.length,
      active: visibleItems.filter((o) => matchesTab(o, "active")).length,
      past: visibleItems.filter((o) => matchesTab(o, "past")).length,
    }),
    [visibleItems],
  );

  if (isLoading) return <PageLoader />;

  return (
    <div className="flex max-h-[calc(100dvh-3.5rem-var(--mobile-nav-height))] min-h-0 flex-col px-4 pt-4 pb-4 lg:max-h-[calc(100dvh-4.5rem)] lg:px-6 lg:pb-6">
      <div className="sticky top-0 z-10 shrink-0 space-y-4 pb-3">
        <PageHeader
          title="My orders"
          description="View your orders, payments, and past orders."
        />

        <div className="flex gap-2">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              onClick={() => setTab(id)}
              className={`relative flex-1 rounded-full py-1.5 text-xs font-semibold transition-colors ${
                tab === id ? "bg-gradient-brand text-white" : "border border-white/10 bg-surface text-white/50"
              }`}
            >
              {label}
              {tabCounts[id] > 0 && (
                <span
                  className={`absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full px-1 text-[9px] font-bold ${
                    tab === id ? "bg-white/20 text-white" : "bg-white/10 text-white/70"
                  }`}
                >
                  {tabCounts[id]}
                </span>
              )}
            </button>
          ))}
        </div>

        {hiddenCount > 0 ? (
          <div className="flex items-center justify-between gap-3 rounded-2xl border border-white/5 bg-surface/70 px-3.5 py-2.5">
            <div className="min-w-0">
              <p className="text-xs font-medium text-white/80">Show cancelled &amp; forfeited</p>
              <p className="text-[10px] text-white/40">
                {showCancelledForfeited
                  ? `${hiddenCount} hidden order${hiddenCount === 1 ? "" : "s"} visible`
                  : `${hiddenCount} order${hiddenCount === 1 ? "" : "s"} hidden`}
              </p>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={showCancelledForfeited}
              aria-label="Show cancelled and forfeited orders"
              onClick={() => setShowCancelledForfeited((v) => !v)}
              className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full p-0.5 transition-colors ${
                showCancelledForfeited ? "bg-brand-pink" : "bg-white/10"
              }`}
            >
              <span
                className={`pointer-events-none h-6 w-6 rounded-full bg-white shadow transition-transform duration-200 ease-in-out ${
                  showCancelledForfeited ? "translate-x-5" : "translate-x-0"
                }`}
              />
            </button>
          </div>
        ) : null}
      </div>

      <div className="mt-4 min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {!items.length ? (
          <EmptyState icon={Package} title="No orders yet" hint="Your purchases and wins show up here." />
        ) : !filtered.length ? (
          <EmptyState
            icon={Package}
            title={`No ${tab} orders`}
            hint={
              hiddenCount > 0 && !showCancelledForfeited
                ? "Turn on “Show cancelled & forfeited” to include those orders."
                : "Try another filter to see more orders."
            }
          />
        ) : (
          <div className="space-y-3 pb-2">
            {filtered.map((order) => {
              const colorKey = customerOrderStatusColorKey(order.status, order.latestReceiptStatus);
              const badgeColor = statusColor(colorKey);
              const statusLabel = customerOrderStatusLabel(order.status, order.latestReceiptStatus);
              const paid = isPostPayment(order.status);
              const showDocIcon = paid && (documentsOn || invoicingOn);
              const awaitingVerification = order.latestReceiptStatus === "PENDING";
              const showPaymentDeadline =
                order.status === "PENDING_PAYMENT" && Boolean(order.paymentDeadline) && !awaitingVerification;
              const amountLabel = order.creditUsed > 0 ? credits(order.creditUsed) : rm(order.total);
              const pendingReviews = reviewableItemCount(order);
              const showReviewPanel = reviewOrderId === order.id && pendingReviews > 0;
              const needsMarketplaceConfirm =
                order.kind === "MARKETPLACE" &&
                order.sellerFulfilmentStatus &&
                !["BUYER_CONFIRMED"].includes(order.sellerFulfilmentStatus) &&
                ["SHIPPED", "READY_FOR_COLLECTION", "COMPLETED"].includes(order.sellerFulfilmentStatus);

              return (
                <article
                  key={order.id}
                  className="overflow-hidden rounded-2xl border border-white/5 bg-surface shadow-sm"
                >
                  <div
                    className={`flex items-center justify-between gap-3 border-b px-4 py-2.5 ${headerBandClass(badgeColor)}`}
                  >
                    <Badge color={badgeColor}>{statusLabel}</Badge>
                    <div className="flex shrink-0 items-center gap-2">
                      {pendingReviews > 0 && (
                        <span className="flex items-center gap-1 rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] font-semibold text-amber-200">
                          <Star size={10} fill="currentColor" />
                          Rate
                        </span>
                      )}
                      {showPaymentDeadline && (
                        <Countdown target={order.paymentDeadline!} compact />
                      )}
                      {showDocIcon && (
                        <button
                          type="button"
                          onClick={() => setDocOrder({ id: order.id, orderNo: order.orderNo })}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
                          aria-label="Download receipt or invoice"
                        >
                          <Receipt size={16} strokeWidth={1.75} />
                        </button>
                      )}
                    </div>
                  </div>

                  <Link
                    to={`/orders/${order.id}`}
                    className="block px-4 py-3.5 transition-colors hover:bg-white/[0.02] active:bg-white/[0.04]"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 space-y-1">
                        <p className="font-mono text-sm font-semibold text-white underline decoration-white/20 underline-offset-2">
                          {order.orderNo}
                        </p>
                        <p className="text-[11px] text-white/45">{fmtOrderListDate(order.createdAt)}</p>
                      </div>
                      <p className="shrink-0 font-display text-sm font-bold text-brand-pink">{amountLabel}</p>
                    </div>
                    {order.fulfilmentMethod === "PICKUP" && order.pickupDetails ? (
                      <PickupScheduleCallout details={order.pickupDetails} compact />
                    ) : null}
                    {order.kind === "MARKETPLACE" && order.sellerFulfilmentStatus && order.status !== "PENDING_PAYMENT" ? (
                      <p className="mt-2 text-[11px] text-muted">
                        {marketplaceFulfilmentMethodLabel(order.fulfilmentMethod, collectLabel)} ·{" "}
                        <span className="font-semibold text-foreground">
                          {marketplaceFulfilmentStatusLabel(
                            order.status === "CANCELLED" ? "CANCELLED" : order.sellerFulfilmentStatus,
                            collectLabel,
                          )}
                        </span>
                        {order.trackingNo && order.fulfilmentMethod !== "PICKUP"
                          ? ` · ${order.courier ?? "Tracking"} ${order.trackingNo}`
                          : ""}
                      </p>
                    ) : null}
                    {(order.kind === "MARKETPLACE" || config?.shopReviewsEnabled) && order.items.some((i) => i.review) && (
                      <div className="mt-2 flex flex-wrap gap-2">
                        {order.items
                          .filter((i) => i.review)
                          .map((i) => (
                            <div
                              key={i.id}
                              className="flex max-w-full items-center gap-1.5 rounded-lg bg-white/5 px-2 py-1"
                            >
                              <StarRating value={i.review!.rating} readOnly size={11} />
                              <span className="truncate text-[10px] text-white/50">{i.name}</span>
                            </div>
                          ))}
                      </div>
                    )}
                  </Link>

                  {(pendingReviews > 0 || needsMarketplaceConfirm) && (
                    <div className="border-t border-white/5 px-4 py-3">
                      {needsMarketplaceConfirm && pendingReviews === 0 && (
                        <p className="mb-2 text-[11px] text-white/50">
                          Confirm receipt on the order page to unlock reviews.
                        </p>
                      )}
                      {pendingReviews > 0 && !showReviewPanel && (
                        <div className="flex flex-wrap items-center gap-2">
                          <Button variant="outline" className="text-xs" onClick={() => setReviewOrderId(order.id)}>
                            Rate {pendingReviews} item{pendingReviews === 1 ? "" : "s"}
                          </Button>
                          <Link
                            to={`/orders/${order.id}`}
                            className="text-[11px] font-medium text-brand-pink hover:underline"
                          >
                            Open order
                          </Link>
                        </div>
                      )}
                      {showReviewPanel && (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between gap-2">
                            <p className="text-xs font-semibold text-white">Your review</p>
                            <button
                              type="button"
                              className="text-[11px] text-white/45 hover:text-white"
                              onClick={() => setReviewOrderId(null)}
                            >
                              Collapse
                            </button>
                          </div>
                          {order.items
                            .filter((i) => i.canReview)
                            .map((item) => (
                              <PurchaseReviewForm
                                key={item.id}
                                orderItemId={item.id}
                                productName={item.name}
                                onSubmitted={invalidateOrders}
                              />
                            ))}
                        </div>
                      )}
                    </div>
                  )}
                </article>
              );
            })}
          </div>
        )}
      </div>

      <OrderDocumentsModal
        order={docOrder}
        documentsOn={documentsOn}
        invoicingOn={invoicingOn}
        onClose={() => setDocOrder(null)}
      />
    </div>
  );
}
